/**
 * The customer-flow step machine.
 *
 * A small, explicit, linear machine over the approved screens. Kept pure (no
 * React, no store) so both the store and components can reason about order,
 * stepper position, and advance-guards without duplication.
 *
 * PLAN-DERIVED (KYCID-FSD-00013 §8): the real ordering is the frozen per-session
 * plan, not a module constant. So `nextStepId`/`prevStepId`/`stepperPosition`
 * now take the derived order as a parameter — DEFAULTED to the legacy module
 * constant, so every existing caller (which passes no order) is byte-for-byte
 * unchanged and still runs today's flow. `flow/checkPlan.ts` builds the derived
 * order from the plan; the store threads it through `completeCurrentAndAdvance`.
 */

import type { CheckRequirement, PlanCheckDTO, StepId } from '@/types';
import type { FlowStepId } from '@/types';
import { checkKeyOfStep, isCheckStepId } from '@/types';

/** Full ordered flow (welcome is the pre-stepper entry screen) — the legacy default. */
export const FLOW_ORDER: readonly FlowStepId[] = [
  'welcome',
  // now-vs-later fork (SPEC-07 §9/F-CA-09): booked customers see their appointment,
  // walk-ins continue straight through. Excluded from the stepper (like welcome).
  'schedule',
  'consent',
  // SPEC-14 §5 — "Confirm your details" (golden-source intake). AFTER consent,
  // BEFORE the document/capture steps. Self-skipping: with no intake served
  // (older be / none configured) the step advances instantly and renders
  // nothing. Excluded from the stepper (renders no Stepper, like the check steps).
  'confirm',
  // Aadhaar rail (SPEC-05, UX_SPEC §3.2(b)): AFTER consent (the rail 422s without a
  // consent record — DPDP ordering), BEFORE capture. Skippable when the provider is
  // unavailable — documents verify on the call instead (honest fallback, never fake).
  'aadhaar',
  'capture',
  'waiting',
  'call',
  'complete',
] as const;

/** Steps that advance the progress bar (welcome + schedule are excluded) — the legacy default. */
export const STEPPER_STEPS: readonly FlowStepId[] = [
  'consent',
  'aadhaar',
  'capture',
  'waiting',
  'call',
  'complete',
] as const;

export const STEPPER_TOTAL = STEPPER_STEPS.length;

/** Index of `step` within `order` (defaults to the legacy FLOW_ORDER). */
export function stepIndex<T extends string = FlowStepId>(
  step: T,
  order: readonly T[] = FLOW_ORDER as readonly T[],
): number {
  return order.indexOf(step);
}

/** Next step in `order` (clamped at the tail). Defaults to the legacy FLOW_ORDER. */
export function nextStepId<T extends string = FlowStepId>(
  step: T,
  order: readonly T[] = FLOW_ORDER as readonly T[],
): T {
  const i = order.indexOf(step);
  if (i === -1) {
    // A persisted step no longer in the plan-derived order (e.g. an agent-driven check hidden by
    // KYCID·00001-00005-00017 across a deploy) must NOT restart the flow at order[0] — advance it to the
    // waiting room (the tail).
    const w = order.indexOf('waiting' as T);
    return w >= 0 ? order[w]! : order[order.length - 1]!;
  }
  return order[Math.min(i + 1, order.length - 1)]!;
}

/** Previous step in `order` (clamped at the head). Defaults to the legacy FLOW_ORDER. */
export function prevStepId<T extends string = FlowStepId>(
  step: T,
  order: readonly T[] = FLOW_ORDER as readonly T[],
): T {
  const i = order.indexOf(step);
  return order[Math.max(i - 1, 0)]!;
}

/**
 * 1-based position within the visible stepper; 0 when the step is not a stepper
 * step (welcome/schedule entry). `stepperSteps` defaults to the legacy constant,
 * so an existing caller re-indexes over today's flow; a plan-derived caller
 * passes `store.stepperSteps` and gets a re-indexed position with no off-by-one.
 */
export function stepperPosition<T extends string = FlowStepId>(
  step: T,
  stepperSteps: readonly T[] = STEPPER_STEPS as readonly T[],
): number {
  return stepperSteps.indexOf(step) + 1;
}

/** Per-check advance state the store keeps while a check step is on screen. */
export type CheckAdvanceState = 'pending' | 'satisfied' | 'fallback' | 'skipped';

/** Advance-guards — simple predicates over UI flags held in the store. */
export interface FlowGuards {
  consentAffirmed: boolean;
  captureQualityOk: boolean;
  /**
   * Per-check advance state keyed by CheckKey. `satisfied` (genuinely verified)
   * and `fallback` (provider-outage / live-OVD on the call) both clear a REQUIRED
   * check; `skipped` clears an OPTIONAL one. Absent = pending.
   */
  checks?: Readonly<Record<string, CheckAdvanceState>>;
}

/**
 * Can the flow advance off `step`?
 *  - a keyed check step: read its clamped requirement from `plan`. REQUIRED
 *    advances ONLY on satisfied|fallback; OPTIONAL/OFF never block.
 *  - a frame step: the legacy consent/capture affirmation guards.
 *
 * `plan` defaults to empty (today's flow has no keyed checks), so a legacy
 * caller passing only (step, guards) is unchanged.
 */
export function canAdvance(
  step: StepId,
  guards: FlowGuards,
  plan: readonly PlanCheckDTO[] = [],
): boolean {
  if (isCheckStepId(step)) {
    const key = checkKeyOfStep(step);
    const requirement: CheckRequirement =
      plan.find((p) => p.checkKey === key)?.requirement ?? 'OPTIONAL';
    if (requirement !== 'REQUIRED') return true; // OPTIONAL/OFF never gate
    const state = guards.checks?.[key];
    return state === 'satisfied' || state === 'fallback';
  }
  switch (step) {
    case 'consent':
      return guards.consentAffirmed;
    case 'capture':
      return guards.captureQualityOk;
    default:
      return true;
  }
}
